import React, { useState, useMemo } from 'react';
import { PlusIcon, CubeIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { usePackages, type Package } from '../../hooks/usePackages';
import PackageCard from './PackageCard';
import PackageBuilder from './PackageBuilder';

export default function PaquetesSection() {
  const { packages, loading, refresh } = usePackages();
  const [search, setSearch] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [tagFilter, setTagFilter] = useState<string | ''>('');

  const allTags = useMemo(() => {
    const set = new Set<string>();
    packages.forEach((p) => {
      (Array.isArray(p.tags) ? p.tags : []).forEach((t: string) => t && set.add(t.trim()));
    });
    return Array.from(set).sort();
  }, [packages]);

  const filtered = packages.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter =
      filter === 'all' ? true : filter === 'active' ? p.is_active : !p.is_active;
    const tags = Array.isArray(p.tags) ? p.tags : [];
    const matchesTag = !tagFilter || tags.some((t: string) => t.trim().toLowerCase() === tagFilter.toLowerCase());
    return matchesSearch && matchesFilter && matchesTag;
  });

  const openBuilder = () => {
    setEditingPackage(null);
    setShowBuilder(true);
  };

  const openEdit = (pkg: Package) => {
    setEditingPackage(pkg);
    setShowBuilder(true);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-64 bg-gray-100 rounded-xl animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Header and search/filter always visible */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Paquetes</h2>
          <button
            type="button"
            onClick={openBuilder}
            className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-purple-700"
          >
            <PlusIcon className="w-4 h-4" />
            Nuevo paquete
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="flex rounded-xl border border-gray-200 overflow-hidden">
            {(['active', 'all', 'inactive'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-3 py-2 text-sm transition-colors ${
                  filter === f
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                {f === 'active' ? 'Activos' : f === 'all' ? 'Todos' : 'Inactivos'}
              </button>
            ))}
          </div>
          {allTags.length > 0 && (
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-4 h-4 text-gray-400" aria-hidden />
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="">Todas las etiquetas</option>
                {allTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mb-4">
            <CubeIcon className="w-8 h-8 text-purple-300" />
          </div>
          <h3 className="text-gray-700 font-medium mb-1">
            {packages.length === 0
              ? 'No hay paquetes aún'
              : search || tagFilter
              ? 'Sin resultados'
              : filter !== 'all'
              ? 'No hay paquetes con este estado'
              : 'Sin resultados'}
          </h3>
          <p className="text-sm text-gray-400 mb-5">
            {packages.length === 0
              ? 'Crea tu primer paquete para vender combos de productos y servicios'
              : 'Prueba otro término de búsqueda o quita filtros'}
          </p>
          {packages.length === 0 && (
            <button
              type="button"
              onClick={openBuilder}
              className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-purple-700"
            >
              <PlusIcon className="w-4 h-4" />
              Crear paquete
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} onEdit={openEdit} />
          ))}
        </div>
      )}

      {showBuilder && (
        <PackageBuilder
          editingPackage={editingPackage}
          onClose={() => {
            setShowBuilder(false);
            setEditingPackage(null);
          }}
          onSaved={() => {
            refresh();
          }}
        />
      )}
    </>
  );
}
